<template>
    <v-card :loading="loading" tile class="elevation-0">
        <div class="card-title" v-if="isAdmin">
            {{ $t('AddContactFields') }}
        </div>
        <router-link v-if="isAdmin" :to="{ name: 'QuestionDropDown' }" custom>
            <v-btn fab small class="white--text float-end" color="secondary" role="link" style="margin: 14px;">
                <v-icon>mdi-arrow-left-circle</v-icon>
            </v-btn>
        </router-link>

        <v-card-text class="pa-0 mt-3">
            <!-- Admin-specific form fields -->
            <v-form ref="contactForm" v-model="valid" lazy-validation v-if="isAdmin">
                <v-row>
                    <v-col cols="12" md="3" class="py-2">
                        <v-text-field class="mt-3" :rules="nameRules"
                                      v-model.lazy="contactItemNameEn"
                                      outlined
                                      hide-detail
                                      dense
                                      :label="$t('QuestionEn')" />
                    </v-col>
                    <v-col cols="12" md="3" class="py-2">
                        <v-text-field class="mt-3" :rules="nameRules"
                                      v-model.lazy="contactItemNameAr"
                                      outlined
                                      hide-detail
                                      dense
                                      :label="$t('QuestionAr')" />
                    </v-col>
                    <v-col cols="12" md="3" class="py-2">
                        <v-autocomplete class="mt-3" :rules="nameRules"
                                        searchable
                                        dense
                                        outlined
                                        v-model.lazy="TypeId"
                                        :items="questionTypes"
                                        :menu-props="{ bottom: true, offsetY: true }"
                                        append-icon="mdi-chevron-down"
                                        item-value="id"
                                        item-text="questionTypeNameEn"
                                        :label="$t('Select')">
                            <template v-slot:item="{ item }">
                                <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                <v-list-item-content>
                                    <h4>{{ language === 'ar' ? item.questionTypeNameAr : item.questionTypeNameEn }}</h4>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="1" class="text-center py-2">
                        <v-btn class="mt-3" tile color="primary" dense @click="AddContactItem" v-if="isAdmin">
                            {{ $t('Add') }}
                        </v-btn>
                    </v-col>
                </v-row>

            </v-form>


            <v-card elevation="0" class="mt-3 rounded-lg" outlined>
                <v-card-text class="py-12">
                    <v-row v-for="(contactFormQuestion, qIndex) in contactFormQuestions" :key="qIndex">
                        <v-col cols="10" md="6" class="py-2">

                            <label>{{ contactFormQuestion.label }}</label>


                            <!-- Display fields based on question type -->
                            <v-textarea v-model="internalValues[qIndex]" :rules="nameRules"
                                        class="mt-3"
                                        v-if="contactFormQuestion.type === shortAnswer || contactFormQuestion.type === textArea"
                                        outlined
                                        hide-detail
                                        dense
                                        :label="$t('DefaultText')"
                                        @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)" />

                            <phoneNumber @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                         :isAdmin="isAdmin"
                                         v-model="internalValues[qIndex]"
                                         class="mt-3" :validate="validate"
                                         v-if="contactFormQuestion.type === PhoneNumber"
                                         :mobileType="mobileType"
                                         outlined />

                            <emailInput @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                        v-model="internalValues[qIndex]"
                                        class="mt-3"
                                        v-if="contactFormQuestion.type === EmailInput"
                                        outlined :required="validate" />

                            <div v-if="contactFormQuestion.type === HijriDate" class="text-center">
                                <hijriDatePicker :rules="nameRules" @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                                 v-model="internalValues[qIndex]" dense outlined />
                            </div>

                            <div v-if="contactFormQuestion.type === Date" class="text-center">
                                <v-date-picker dense outlined  :rules="nameRules"
                                               @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                               v-model="internalValues[qIndex]" />
                            </div>

                            <div v-if="contactFormQuestion.type === multipleChoice || contactFormQuestion.type === checkbox || contactFormQuestion.type === dropdown">
                                <v-autocomplete @change="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                                v-model="internalValues[qIndex]"
                                                dense
                                                outlined
                                                v-if="contactFormQuestion.type === dropdown"
                                                :items="contactFormQuestion.options"  :rules="nameRules"
                                                item-value="id"
                                                item-text="optionEn"
                                                :label="$t('Select')" />

                                <v-radio-group @change="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"  :rules="nameRules"
                                               v-model="internalValues[qIndex]"
                                               v-if="contactFormQuestion.type === multipleChoice">
                                    <v-radio v-for="(option, optIndex) in contactFormQuestion.options" :key="optIndex" :label="option.optionEn" :value="option.id" />
                                </v-radio-group>

                                <template v-if="contactFormQuestion.type === checkbox">
                                    <ArrayCheckboxList @input="emitCombinedValue(contactFormQuestion.label, internalValues[qIndex], qIndex)"
                                                       v-model="internalValues[qIndex]"
                                                       :options="getOptions(contactFormQuestion.options)" @validation-state="handleCheckboxValidation"
                                                       dense />
                                </template>

                                <template v-if="contactFormQuestion.type === multipleChoice || contactFormQuestion.type === checkbox || contactFormQuestion.type === dropdown">
                                    <v-row v-if="isAdmin">
                                        <v-col cols="12" md="12" class="text-start">
                                            <v-btn rounded
                                                   class="mt-2"
                                                   outlined
                                                   @click="addOption(contactFormQuestion)">
                                                <v-icon size="20" class="me-3">mdi-plus-circle-outline</v-icon>
                                                {{ $t('AddOptions') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                    <v-row v-for="(item, optIndex) in contactFormQuestion.options" :key="optIndex">
                                        <v-col md="5" v-if="isAdmin">
                                            <v-text-field outlined
                                                          v-model.lazy="item.optionAr"
                                                          hide-details
                                                          dense
                                                          :label="$t('TextAr')" />
                                        </v-col>
                                        <v-col md="5" v-if="isAdmin">
                                            <v-text-field outlined
                                                          v-model.lazy="item.optionEn"
                                                          hide-details
                                                          dense
                                                          :label="$t('TextEn')" />
                                        </v-col>
                                        <v-col md="2">
                                            <v-icon class="mt-2" v-if="isAdmin"
                                                    @click="removeOption(optIndex, contactFormQuestion.options)">
                                                mdi-close
                                            </v-icon>
                                        </v-col>
                                    </v-row>
                                </template>
                            </div>
                        </v-col>
                        <v-col cols="1" md="1">
                            <v-icon class="mt-6" @click="removeQuestion(qIndex)" color="error" v-if="isAdmin">
                                mdi-trash-can-outline
                            </v-icon>
                        </v-col>

                    </v-row>
                    <v-row v-if="isAdmin">
                        <v-col cols="12">
                            <v-btn class="mt-3 float-end" tile color="primary" dense @click="SaveForm">
                                {{ $t('Save') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-card-text>

    </v-card>
</template>

<script>
    import { mapGetters } from 'vuex'
    import ContactFormService from '@/services/ContactFormService.js'
    import LookupAdminService from '@/services/LookupAdminServices.js'
    import hijriDatePicker from '@/components/controls/Hijri.vue'
    // import DatePicker from '@/components/controls/DatePicker.vue'
    import phoneNumber from '@/components/controls/phoneNumber.vue'
    import emailInput from '@/components/controls/EmailInput.vue'
    import ArrayCheckboxList from '@/components/controls/ArrayCheckboxList.vue'
    import { FormMethodEnum } from '@/helpers/constants/enumerations.js'

    export default {
        components: {
            // DatePicker,
            hijriDatePicker,
            phoneNumber,
            emailInput,
            ArrayCheckboxList
        },
        props: {
            value: {
                type: String,
                default: null,
            },
            contactFormControl: {
                type: String,
                default: ''
            },
            isAdmin: {
                type: Boolean,
                default: true
            },
            validate: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                valid: true,
                nameRules: [
                    v => !!v || this.$t('RequiredField') || this.validate == true
                ],
                shortAnswer: FormMethodEnum.ShortAnswer,
                checkbox: FormMethodEnum.Checkboxes,
                multipleChoice: FormMethodEnum.MultipleChoice,
                dropdown: FormMethodEnum.Dropdown,
                date: FormMethodEnum.Date,
                textArea: FormMethodEnum.TextArea,
                HijriDate: FormMethodEnum.HijriDate,
                PhoneNumber: FormMethodEnum.PhoneNumber,
                EmailInput: FormMethodEnum.EmailInput,
                contactItemNameEn: '',
                contactItemNameAr: '',
                TypeId: null,
                inputTextAr: '',
                inputTextEn: '',
                itemContact: { options: [] },
                questionTypes: [],
                loading: false,
                internalValues: [],
                contactFormQuestions: [],
                mobileType: null,
                isCheckboxValid:true,
            }
        },
        created() {
            console.log('Component created')
            this.listContactForm()
            if (this.isAdmin) {
                this.getquestionTypes()
            }

        },
        computed: {
            ...mapGetters('user', ['language']),
            getLanguage() {
                return this.language || 'ar'
            },
            emailRules() {
                return [
                    v => !!v || this.$t('Email is required'),
                    v => /.+@.+\..+/.test(v) || this.$t('Email must be valid')
                ]
            },
            mobileRules() {
                return [
                    v => !!v || this.$t('Mobile number is required'),
                    v => /^\d{10}$/.test(v) || this.$t('Mobile number must be 10 digits')
                ]
            }
        },
        watch: {
            internalValues: {
                handler(newValues) {
                    newValues.forEach((value, index) => {
                        const question = this.contactFormQuestions[index]
                        if (question && question.type === this.PhoneNumber) {
                            question.option = value 
                        }
                    })
                },
                deep: true 
            }
        },
        methods: {
            handleCheckboxValidation(isValid) {
                this.$emit('validation-state', isValid)
            },
            listContactForm() {
                this.loading = true
                ContactFormService.listContactForm()
                    .then(res => {
                        this.contactFormQuestions = res.data
                        for (var i = 0; i < this.contactFormQuestions.length; i++) {
                            
                            if (this.contactFormQuestions[i].type === this.PhoneNumber) {
                               this.$set(this.internalValues, i, this.contactFormQuestions[i].option)

                               this.mobileType=this.contactFormQuestions[i].option
                            }

                        }
                        try {
                            var jsonData = JSON.parse(this.value)
                            Object.entries(jsonData).forEach(([key, value]) => {
                                for (var i = 0; i < this.contactFormQuestions.length; i++) {
                                    if (this.contactFormQuestions[i].label === key) {
                                        if (this.contactFormQuestions[i].type === this.dropdown || this.contactFormQuestions[i].type === this.multipleChoice) {
                                            this.$set(this.internalValues, i, this.contactFormQuestions[i].options.find(q => q.optionAr === value)?.id || null)
                                        } else if (this.contactFormQuestions[i].type === this.checkbox) {
                                            const selectedOptions = this.contactFormQuestions[i].options.filter(q => value.includes(q.optionAr))
                                            const selectedIds = selectedOptions.map(option => option.id)
                                            this.$set(this.internalValues, i, selectedIds)
                                        } else {
                                            this.$set(this.internalValues, i, value)
                                        }
                                    }
                                }
                            })


                        }
                        catch {

                        }
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            emitCombinedValue(label, value, ind) {

                if (this.contactFormQuestions[ind].type == this.dropdown || this.contactFormQuestions[ind].type == this.multipleChoice) {
                    value = this.contactFormQuestions[ind].options.find(q => q.id === value)?.optionEn || value
                } else if (this.contactFormQuestions[ind].type == this.checkbox) {
                    value = this.contactFormQuestions[ind].options.find(q => q.id === value[0])?.optionEn || value
                }
                var data = ''
                for (var i = 0; i < this.contactFormQuestions.length; i++) {
                    var val = ''
                    if (this.contactFormQuestions[i].type == this.dropdown || this.contactFormQuestions[i].type == this.multipleChoice) {
                        val = this.contactFormQuestions[i].options.find(q => q.id === this.internalValues[i])?.optionAr || this.internalValues[i]
                    } else if (this.contactFormQuestions[i].type == this.checkbox) {

                        var found = this.contactFormQuestions[i].options.filter(q => this.internalValues[i].filter(s => s === q.id).length > 0)
                        val = ''
                        for (var z = 0; z < found.length; z++) {
                            val += ',' + found[z].optionAr
                        }
                        if (val != '')
                            val = val.substring(1)
                    }
                    else {
                        val = this.internalValues[i]
                    }
                    data += ',"' + this.contactFormQuestions[i].label + '":"' + val + '"'
                }
                if (data != '')
                    data = data.substring(1)
                const combinedValue = '{' + data + '}'
                this.$emit('input', combinedValue)
            },
            getOptions(contactFormOption) {
                return contactFormOption.map(option => ({
                    id: option.id,
                    name: option.optionEn
                }))
            },
            getquestionTypes() {
                LookupAdminService.listQuestionTypeItems()
                    .then(res => {
                        this.questionTypes = res.data.filter(item =>
                            [this.shortAnswer, this.textArea, this.checkbox, this.multipleChoice, this.dropdown, this.HijriDate, this.PhoneNumber, this.EmailInput].includes(item.id)
                        )
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            AddContactItem() {
                var valid = this.$refs.contactForm.validate()
                if (valid) {
                    const newFormItem = {
                        type: this.TypeId,
                        label: this.contactItemNameEn,
                        labelAr: this.contactItemNameAr,
                        options: [],//this.itemContact.options,
                        order: this.contactFormQuestions.length + 1
                    }
                    this.contactFormQuestions.push(newFormItem)
                    this.loading = false
                    this.valid = true
                    this.reset()
                    //ContactFormService.AddContactItem(newFormItem)
                    //    .then(() => {
                    //        this.listContactForm()

                    //    })
                    //    .catch(() => {
                    //        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    //    })
                    //    .finally(() => {
                    //        this.loading = false
                    //        this.valid = true
                    //        this.reset()
                    //    })
                }

            },
            SaveForm() {
                ContactFormService.UpdateContactItem(this.contactFormQuestions)
                    .then(() => {
                        this.listContactForm()
                        this.$root.$notification(this.$t('SaveSuccess'), 'success')
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.loading = false
                        this.reset()
                    })
                var a = this.contactFormQuestions
            },
            Save() {
                ContactFormService.AddContactItem(this.contactFormQuestions)
                    .then(() => {
                        this.listContactForm()

                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            reset() {
                this.TypeId = null
                this.contactItemNameEn = ''
                this.contactItemNameAr = ''
            },
            addOption(contactFormQuestion) {
                const option = { optionAr: this.inputTextAr, optionEn: this.inputTextEn, order: contactFormQuestion.options.length + 1 }
                contactFormQuestion.options.push(option)
                this.inputTextAr = ''
                this.inputTextEn = ''
            },
            removeOption(index, options) {
                options.splice(index, 1)
            },
            removeQuestion(index) {
                const questionContactForm = this.contactFormQuestions[index]
                ContactFormService.DeleteContactItem(questionContactForm.id)
                    .then(() => {
                        this.listContactForm()
                    })
                    .catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            parseContactFormControl(contactFormControl) {
                return contactFormControl.split('#')
                    .filter(item => item.trim() !== '')
                    .map(item => {
                        const [label, value] = item.split(':')
                        return { label: label || '', value: value || '', type: null, options: [] }
                    })
            }
        }
    }
</script>

<style>
    .btnSave {
        padding: 10px 20px;
        margin: 10px 20px;
        border: none;
        cursor: pointer;
        float: left;
    }

        .btnSave:hover {
            background-color: #0056b3;
        }
</style>
